import { FlatReportOwners } from 'components/ChatBV/types/DeepDiveReportType';
import {
  FlatPropertyReportPossibleResidents,
  FlatPropertyReportOverallAttributes,
  FlatPropertyReportRoomCounts,
  FlatPropertyReportInteriorDimensions,
  FlatPropertyReportLotDetails,
  FlatPropertyReportLotAndBuildingDetails,
  FlatPropertyReportLocationDetails,
  FlatPropertyReportPossibleCountyAssesorRecords,
  FlatPropertyReportPropertyDetails,
  FlatPropertyReportBuildingPermits,
  FlatPropertyReportHomeOwnerAssociation,
  FlatPropertyReportTaxHistoryEntry,
  FlatPropertyReportAssessedValueAndTaxes,
  FlatPropertyReportPropertyValuation,
  FlatPropertyReportSchool,
  FlatPropertyReportMarketValue,
  FlatPropertyReportRentalValue,
  FlatPropertyReport,
} from 'components/ChatBV/types/DeepDiveReportType/propertyTypes';

import { normalizeValue, normalizeArray, formatDate } from '../generalUtils';

export const mapPropertyReportToFlatSections = (
  reportData: any
): FlatPropertyReport => {
  const property = reportData?.entities?.properties?.[0];
  const building = property?.buildings?.[0];
  const assessment = property?.values?.assessments?.[0];
  const equity = property?.values?.equities?.[0];
  const loan = equity?.loans?.[0];

  // Calculate total rooms
  const totalRooms =
    (building?.rooms?.baths?.total || 0) + (building?.rooms?.bed || 0);

  // Filter market values by source (BK, CA, ARCH) and take first 3 of each
  // Preserves all fields: apn, date, improvements, land, max, min, score, source, standard_deviation, total, year
  const allMarketValues = property?.values?.market || [];
  const allRentalValues = property?.values?.rental || [];
  const sources = ['BK', 'CA', 'ARCH'];
  const filteredMarketValues: FlatPropertyReportMarketValue[] = [];
  let filteredRentalValues: FlatPropertyReportRentalValue[] = [];

  sources.forEach((source) => {
    const sourceValues: FlatPropertyReportMarketValue[] = allMarketValues
      .filter((m: any) => m?.source?.toUpperCase() === source)
      .slice(0, 3)
      .map((m: any) => ({
        apn: normalizeValue(m?.apn),
        date: normalizeValue(m?.date),
        land: normalizeValue(m?.land),
        max: normalizeValue(m?.max),
        min: normalizeValue(m?.min),
      }));
    filteredMarketValues.push(...sourceValues);
  });

  filteredRentalValues = allRentalValues.slice(0, 3).map((r: any) => ({
    max_value: normalizeValue(r?.max_value),
    min_value: normalizeValue(r?.min_value),
    valuation_date: normalizeValue(r?.valuation_date),
    value: normalizeValue(r?.value),
  }));

  // Calculate taxes (sum of all tax bills or use latest)
  const taxBills = property?.taxes?.bills || [];
  // Map tax history from assessments
  const taxHistoryEntries: FlatPropertyReportTaxHistoryEntry[] | undefined =
    property?.values?.assessments?.map((assess: any) => ({
      year: normalizeValue(assess.year),
      land_value: normalizeValue(assess.land),
      improvement_value: normalizeValue(assess.improvements),
      assessed_value: normalizeValue(assess.total),
    }));

  // Map possible residents
  const possibleResidents: FlatPropertyReportPossibleResidents[] | undefined =
    normalizeArray(
      property?.residents?.map((resident: any) => {
        const firstSeen =
          resident?.addresses?.[0]?.meta?.first_seen_date?.parsed;
        const lastSeen = resident?.addresses?.[0]?.meta?.last_seen_date?.parsed;

        return {
          name: normalizeValue(resident?.names?.[0]?.full),
          location: normalizeValue(resident?.addresses?.[0]?.full),
          first_seen_date: formatDate(firstSeen?.month, firstSeen?.year),
          last_seen_date: formatDate(lastSeen?.month, lastSeen?.year),
          aliases: normalizeArray(
            resident?.names?.slice(1)?.map((n: any) => n.full) || []
          ),
          phones: normalizeArray(
            resident?.phones?.map((p: any) => p.number || p) || []
          ),
        };
      }) || []
    );

  // Map schools (first 3)
  const schools: FlatPropertyReportSchool[] | undefined = normalizeArray(
    reportData?.supporting?.area?.schools?.slice(0, 3)?.map((school: any) => ({
      name: normalizeValue(school.name),
      grades: normalizeValue(school.grades),
      address: normalizeValue(school.address?.full || school.address),
      ...school,
    })) || []
  );

  return {
    permalink: normalizeValue(reportData?.meta?.permalink),
    identity_or_owner_identity_info: {
      owners: normalizeArray(
        property?.owners?.map((owner: any) => ({
          name: normalizeValue(owner?.names?.[0]?.full),
          address: normalizeValue(owner?.addresses?.[0]?.full),
        })) || []
      ) as FlatReportOwners[] | undefined,
    },

    property_or_address_info: {
      possible_residents: possibleResidents,
      property_details: {
        total_finished_square_feet: normalizeValue(
          building?.square_footage?.finished?.total
        ),
        lot_size: normalizeValue(property?.dimensions?.size),
        apn: normalizeValue(property?.apn),
        zoning: normalizeValue(property?.legal?.zoning),
        lot_and_building_details: {
          overall_attributes: {
            total_square_feet: normalizeValue(
              building?.square_footage?.finished?.total
            ),
            number_of_stories: normalizeValue(building?.number_of_stories),
            number_of_units: normalizeValue(building?.number_of_units),
            year_built: normalizeValue(building?.year_built),
            style: normalizeValue(building?.style),
            class: normalizeValue(building?.class),
            construction_type: normalizeValue(building?.construction_type),
          } as FlatPropertyReportOverallAttributes,
          room_counts: {
            total_rooms: normalizeValue(totalRooms),
            beds: normalizeValue(building?.rooms?.bed),
            baths: normalizeValue(building?.rooms?.baths?.total),
            basement: normalizeValue(building?.rooms?.baths?.basement?.full),
          } as FlatPropertyReportRoomCounts,
          interior_dimensions: {
            additions_square_feet: normalizeValue(
              building?.square_footage?.additions
            ),
            attics: normalizeValue(building?.square_footage?.attics),
            garage: normalizeValue(building?.square_footage?.garage),
            basement: normalizeValue(building?.square_footage?.basement?.total),
          } as FlatPropertyReportInteriorDimensions,
          lot_details: {
            county_land_use_code: normalizeValue(
              property?.legal?.land_use?.land_use_code
            ),
            buildings: normalizeValue(property?.buildings?.length),
            lot_frontage: normalizeValue(property?.dimensions?.width),
            lot_depth: normalizeValue(property?.dimensions?.depth),
          } as FlatPropertyReportLotDetails,
        } as FlatPropertyReportLotAndBuildingDetails,
        room_counts: {
          total_rooms: normalizeValue(totalRooms),
          beds: normalizeValue(building?.rooms?.bed),
          baths: normalizeValue(building?.rooms?.baths?.total),
          basement: normalizeValue(building?.rooms?.baths?.basement?.full),
        } as FlatPropertyReportRoomCounts,
        interior_dimensions: {
          additions_square_feet: normalizeValue(
            building?.square_footage?.additions
          ),
          attics: normalizeValue(building?.square_footage?.attics),
          garage: normalizeValue(building?.square_footage?.garage),
          basement: normalizeValue(building?.square_footage?.basement?.total),
        } as FlatPropertyReportInteriorDimensions,
        lot_details: {
          county_land_use_code: normalizeValue(
            property?.legal?.land_use?.land_use_code
          ),
          buildings: normalizeValue(property?.buildings?.length),
          lot_frontage: normalizeValue(property?.dimensions?.width),
          lot_depth: normalizeValue(property?.dimensions?.depth),
        } as FlatPropertyReportLotDetails,
        location_details: {
          state: normalizeValue(property?.parcel_address?.parsed?.state),
          county: normalizeValue(property?.parcel_address?.county?.name),
          fips: normalizeValue(property?.parcel_address?.county?.fips),
          duplicate_apn: normalizeValue(property?.alternate_apn),
          description: normalizeValue(property?.legal?.brief_description),
          subdivision: normalizeValue(property?.legal?.subdivision),
          tract_number: normalizeValue(property?.legal?.tract_number),
          block: normalizeValue(property?.legal?.block),
          section: normalizeValue(property?.legal?.section),
          lot_number: normalizeValue(property?.legal?.lot_numbers?.[0]),
          unit: normalizeValue(property?.legal?.unit),
          latitude: normalizeValue(property?.parcel_address?.geo?.latitude),
          longitude: normalizeValue(property?.parcel_address?.geo?.longitude),
        } as FlatPropertyReportLocationDetails,
        possible_county_assesor_records: {
          owner_occupied: normalizeValue(property?.owner_occupied),
          owner_vesting: normalizeValue(property?.owner_vesting),
          tax_bill_address: normalizeValue(property?.mailing_address?.full),
        } as FlatPropertyReportPossibleCountyAssesorRecords,
      } as FlatPropertyReportPropertyDetails,

      building_permits: normalizeArray(
        property?.permits?.map((permit: any) => ({
          permit_type: normalizeValue(permit?.permit_type),
          sub_type: normalizeValue(permit?.sub_type),
          permit_number: normalizeValue(permit?.permit_number),
          permit_requestor_name: normalizeValue(permit?.permit_requestor_name),
          description: normalizeValue(permit?.description),
          publication_date: normalizeValue(permit?.publication_date),
          fees: normalizeValue(permit?.fees),
          owner_name: normalizeValue(permit?.owner_name),
          status: normalizeValue(permit?.status),
          current_location: normalizeValue(permit?.current_location),
        })) || []
      ) as FlatPropertyReportBuildingPermits[] | undefined,

      // HOA
      home_owner_associations: normalizeArray(
        property?.hoas?.map((hoa: any) => ({
          name: normalizeValue(hoa?.name),
          fee: normalizeValue(hoa?.fee),
        })) || []
      ) as FlatPropertyReportHomeOwnerAssociation[] | undefined,

      // TAX_ASSESSMENT: Property tax assessment and taxes
      tax_assessment: {
        land_value: normalizeValue(assessment?.land),
        improvement_value: normalizeValue(assessment?.improvements),
        total_value_assessed: normalizeValue(assessment?.total),
        taxes: normalizeArray(
          taxBills?.slice(0, 3).map((tax: any) => tax?.amount)
        ),
        entries: normalizeArray(taxHistoryEntries || []),
      } as FlatPropertyReportAssessedValueAndTaxes,

      // VALUATION: Market valuation (property market values, vehicle trade-in values)
      valuation: {
        market: normalizeArray(filteredMarketValues),
        rental: normalizeArray(filteredRentalValues),
        equities: equity
          ? {
              est_remaining_mortgage: normalizeValue(loan?.value),
              equity: normalizeValue(equity?.estimated_available_equity),
              lendable_amount: normalizeValue(
                equity?.estimated_lendable_equity
              ),
              est_loan_to_value: normalizeValue(equity?.altv),
            }
          : undefined,
      } as FlatPropertyReportPropertyValuation,

      // SCHOOLS
      schools,
    },
  };
};
